/** Minimal fields needed to format a member/user display name. */
export type MemberNameSource = {
  email?: string
  pending?: boolean
  firstName?: string
  lastName?: string
}

function emailOrEmpty(member: MemberNameSource): string {
  return member.email ?? ''
}

/** Full display label — `"First Last"` when accepted, otherwise email. */
export function memberFullName(member: MemberNameSource): string {
  if (member.pending) {
    return emailOrEmpty(member)
  }
  if (member.firstName != null && member.lastName) {
    return `${member.firstName} ${member.lastName}`
  }
  if (member.firstName != null) {
    return member.firstName
  }
  return emailOrEmpty(member)
}

/** First name when accepted, otherwise email. */
export function memberShortName(member: MemberNameSource): string {
  if (member.pending) {
    return emailOrEmpty(member)
  }
  if (member.firstName != null) {
    return member.firstName
  }
  return emailOrEmpty(member)
}

/** Avatar initials — name initials when accepted, otherwise first char of email local-part. */
export function memberInitials(member: MemberNameSource): string {
  if (member.pending || member.firstName == null) {
    const email = emailOrEmpty(member)
    const localPart = email.split('@')[0] ?? email
    return (localPart[0] ?? '?').toLocaleUpperCase()
  }
  const lastInitial = member.lastName?.[0] ?? ''
  return `${member.firstName[0] ?? ''}${lastInitial}`.toLocaleUpperCase()
}

/** Secondary line under the name — `null` when pending or when there is no email. */
export function memberSecondaryInformation(member: MemberNameSource): string | null {
  if (member.pending) {
    return null
  }
  return member.email ?? null
}

/** `"First L."` when accepted with a last name, otherwise the first name or email. */
export function memberAbbreviatedName(member: MemberNameSource): string {
  if (member.pending || member.firstName == null) {
    return emailOrEmpty(member)
  }
  if (!member.lastName) {
    return member.firstName
  }
  return `${member.firstName} ${member.lastName[0]}.`
}

export function compareMembersByName(a: MemberNameSource, b: MemberNameSource): number {
  return memberFullName(a).localeCompare(memberFullName(b))
}

export function memberMatchesQuery(member: MemberNameSource, query: string): boolean {
  const q = query.toLowerCase().trim()
  if (!q) return true

  if (member.email?.toLowerCase().includes(q)) {
    return true
  }
  if (member.pending) {
    return false
  }
  return (
    (member.firstName?.toLowerCase().includes(q) ?? false) ||
    (member.lastName?.toLowerCase().includes(q) ?? false)
  )
}
